/**
 * The map's look (docs/UI.md "The look of the map"): how the city bitmap the minimap and the full map scale is
 * painted — the base (water, greens, yards, the viaduct), the blocks, the roads by class — and the palette the
 * captions use. One painter per look:
 *
 *  night    THE look (the owner's "A with C's roofs"): modern and dark like a GTA radar — roads by class with a
 *           soft glow, crisp white type, a white minimap ring — with the painted roofs under it, muted into the
 *           night palette: slate and tar by style, shadows cast by height, trees on the greens, ripples on the
 *           water. The roof DETAIL (ridges, chimneys, sawtooth bays, lamps) is not in the bitmap: `drawRoofDetail`
 *           draws it on the full map from the layout, fading in as you zoom in, so zoomed out the map stays calm.
 *  classic  the first map: dark ground, tan roads, flat blocks (`?maplook=classic`, the fallback)
 *  print    a 1970 London A–Z / street atlas: cream paper, a pastel per district, A-roads in orange, black type
 *  painted  the illustrated top-down the roofs came from, in full colour
 *
 * Baked once (`bakeCity`) at ~2.6 px per metre into one 2048 px canvas (16 MB), plus a 1024 px copy for the
 * minimap (4 MB; calmer, and a quarter of the texture to sample every frame). Nothing here runs per frame.
 * `mapLookFromUrl` reads `?maplook=`; the default is LOOK_DEFAULT.
 */
import type { Rect } from '../core/math';
import type { BuildingLot, CityLayout, MapFeature, RoadEdge } from '../world/types';

export type MapLook = 'classic' | 'night' | 'print' | 'painted';
export const LOOKS: readonly MapLook[] = ['classic', 'night', 'print', 'painted'];
export const LOOK_DEFAULT: MapLook = 'night';

/** What the captions and the chrome draw in, per look (the painters own the bitmap's colours). */
export interface MapPalette {
  /** Behind the bitmap (outside the borough) and the minimap's ground. */
  bg: string;
  /** Plain caption text and its halo. */
  text: string;
  halo: string;
  /** District names (big, faint) and their halo. */
  district: string;
  districtHalo: string;
  /** Street names along the road, and their halo. */
  street: string;
  streetHalo: string;
  /** The shops' names. */
  poiName: string;
  /** Font families: captions, district names, street names. */
  textFont: string;
  districtFont: string;
  streetFont: string;
  /** District names in capitals? */
  districtCaps: boolean;
  /** Your arrow's halo on the full map. */
  youHalo: string;
}

const ANTON = '"BH Anton", "BH Symbols", Impact, sans-serif';
const OSWALD = '"BH Oswald", "BH Symbols", "Arial Narrow", sans-serif';
const ELITE = '"BH Elite", "BH Symbols", "Courier New", monospace';

export const PALETTES: Record<MapLook, MapPalette> = {
  classic: {
    bg: '#1d1a17',
    text: '#e9e0c9',
    halo: 'rgba(21,18,15,0.88)',
    district: 'rgba(233,224,201,0.55)',
    districtHalo: 'rgba(21,18,15,0.55)',
    street: '#15120f',
    streetHalo: 'rgba(233,224,201,0.85)',
    poiName: '#e9e0c9',
    textFont: ELITE,
    districtFont: ANTON,
    streetFont: ELITE,
    districtCaps: true,
    youHalo: 'rgba(233,224,201,0.22)',
  },
  night: {
    bg: '#0f1115',
    text: '#f4f6f8',
    halo: 'rgba(8,10,14,0.92)',
    district: 'rgba(224,230,238,0.42)',
    districtHalo: 'rgba(8,10,14,0.55)',
    street: '#eef1f5',
    streetHalo: 'rgba(8,10,14,0.88)',
    poiName: '#c9d0d8',
    textFont: OSWALD,
    districtFont: OSWALD,
    streetFont: OSWALD,
    districtCaps: true,
    youHalo: 'rgba(255,255,255,0.18)',
  },
  print: {
    bg: '#e9e1cc',
    text: '#1d1915',
    halo: 'rgba(245,239,224,0.9)',
    district: 'rgba(40,32,26,0.62)',
    districtHalo: 'rgba(245,239,224,0.5)',
    street: '#1d1915',
    streetHalo: 'rgba(255,252,243,0.92)',
    poiName: '#1d1915',
    textFont: OSWALD,
    districtFont: ANTON,
    streetFont: OSWALD,
    districtCaps: true,
    youHalo: 'rgba(29,25,21,0.14)',
  },
  painted: {
    bg: '#2c2a26',
    text: '#f1e9d6',
    halo: 'rgba(20,16,12,0.88)',
    district: 'rgba(241,233,214,0.5)',
    districtHalo: 'rgba(20,16,12,0.6)',
    street: '#f1e9d6',
    streetHalo: 'rgba(20,16,12,0.85)',
    poiName: '#f1e9d6',
    textFont: OSWALD,
    districtFont: ANTON,
    streetFont: OSWALD,
    districtCaps: true,
    youHalo: 'rgba(241,233,214,0.22)',
  },
};

/** `?maplook=night|print|painted` (the mockups), else the default. */
export function mapLookFromUrl(): MapLook {
  try {
    const v = new URLSearchParams(location.search).get('maplook') as MapLook | null;
    return v && LOOKS.includes(v) ? v : LOOK_DEFAULT;
  } catch {
    return LOOK_DEFAULT;
  }
}

export interface BakedCity {
  canvas: HTMLCanvasElement;
  /** Bitmap px per metre. */
  S: number;
  minX: number;
  minZ: number;
  /** A half-size copy for the minimap (a quarter of the pixels to sample each frame; the fine detail averages out). */
  mini: HTMLCanvasElement;
  miniS: number;
}

/** Paint the city once for `look` (the minimap and the full map scale this bitmap). */
export function bakeCity(layout: CityLayout, look: MapLook): BakedCity | null {
  const b = layout.bounds;
  const w = b.maxX - b.minX;
  const h = b.maxZ - b.minZ;
  const S = Math.min(3, 2048 / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(w * S);
  canvas.height = Math.ceil(h * S);
  const g = canvas.getContext('2d');
  if (!g) return null;
  const p = new Painter(g, layout, S);
  if (look === 'night') paintNight(p);
  else if (look === 'print') paintPrint(p);
  else if (look === 'painted') paintPainted(p);
  else paintClassic(p);
  const mini = document.createElement('canvas');
  mini.width = Math.ceil(canvas.width / 2);
  mini.height = Math.ceil(canvas.height / 2);
  const mg = mini.getContext('2d');
  if (mg) {
    mg.imageSmoothingEnabled = true;
    mg.imageSmoothingQuality = 'high';
    mg.drawImage(canvas, 0, 0, mini.width, mini.height);
  }
  return { canvas, S, minX: b.minX, minZ: b.minZ, mini, miniS: S / 2 };
}

/** Bytes the baked bitmaps take as textures (RGBA), for the perf read-out. */
export function bakedBytes(b: BakedCity): number {
  return (b.canvas.width * b.canvas.height + b.mini.width * b.mini.height) * 4;
}

// --------------------------------------------------------------------------- shared painting

type RoadClass = 'main' | 'street' | 'narrow' | 'alley';
const roadClass = (e: RoadEdge): RoadClass => (e.alley ? 'alley' : e.main ? 'main' : e.width >= 9 ? 'street' : 'narrow');

class Painter {
  readonly minX: number;
  readonly minZ: number;
  readonly W: number;
  readonly H: number;
  /** A small seeded stream for the trees and the hatching (the same picture every bake). */
  private seed = 1971;

  constructor(
    readonly g: CanvasRenderingContext2D,
    readonly L: CityLayout,
    readonly S: number,
  ) {
    this.minX = L.bounds.minX;
    this.minZ = L.bounds.minZ;
    this.W = g.canvas.width;
    this.H = g.canvas.height;
  }

  rnd(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  x(wx: number): number {
    return (wx - this.minX) * this.S;
  }

  z(wz: number): number {
    return (wz - this.minZ) * this.S;
  }

  rect(r: Rect): [number, number, number, number] {
    return [this.x(r.minX), this.z(r.minZ), (r.maxX - r.minX) * this.S, (r.maxZ - r.minZ) * this.S];
  }

  fillAll(colour: string): void {
    this.g.fillStyle = colour;
    this.g.fillRect(0, 0, this.W, this.H);
  }

  fillRect(r: Rect, colour: string): void {
    this.g.fillStyle = colour;
    this.g.fillRect(...this.rect(r));
  }

  strokeRect(r: Rect, colour: string, lw: number): void {
    const [x, y, w, h] = this.rect(r);
    this.g.strokeStyle = colour;
    this.g.lineWidth = lw;
    this.g.strokeRect(x + lw / 2, y + lw / 2, w - lw, h - lw);
  }

  /** Diagonal hatching inside a rect (yards, sites). */
  hatch(r: Rect, colour: string, gapM: number, lw: number): void {
    const [x, y, w, h] = this.rect(r);
    const g = this.g;
    g.save();
    g.beginPath();
    g.rect(x, y, w, h);
    g.clip();
    g.strokeStyle = colour;
    g.lineWidth = lw;
    g.beginPath();
    const gap = gapM * this.S;
    for (let d = -h; d < w + h; d += gap) {
      g.moveTo(x + d, y);
      g.lineTo(x + d + h, y + h);
    }
    g.stroke();
    g.restore();
  }

  /** Round dots scattered in a rect (trees on a green): `perM2` per square metre. */
  dots(r: Rect, colour: string, perM2: number, radiusM: number, highlight?: string): void {
    const g = this.g;
    const n = Math.round((r.maxX - r.minX) * (r.maxZ - r.minZ) * perM2);
    for (let i = 0; i < n; i++) {
      const wx = r.minX + 1.5 + this.rnd() * (r.maxX - r.minX - 3);
      const wz = r.minZ + 1.5 + this.rnd() * (r.maxZ - r.minZ - 3);
      const rr = radiusM * (0.75 + this.rnd() * 0.5) * this.S;
      g.fillStyle = colour;
      g.beginPath();
      g.arc(this.x(wx), this.z(wz), rr, 0, Math.PI * 2);
      g.fill();
      if (highlight) {
        g.fillStyle = highlight;
        g.beginPath();
        g.arc(this.x(wx) - rr * 0.3, this.z(wz) - rr * 0.3, rr * 0.45, 0, Math.PI * 2);
        g.fill();
      }
    }
  }

  /** Every feature of a kind, through `f`. */
  features(kind: MapFeature['kind'], f: (r: Rect, name: string | undefined) => void): void {
    for (const ft of this.L.features ?? []) if (ft.kind === kind) f(ft.rect, ft.name);
  }

  /** Each block in its own frame (origin at the centre, rotated); `f` gets the half sizes in px. */
  blocks(f: (hx: number, hz: number, b: BuildingLot) => void): void {
    const g = this.g;
    for (const b of this.L.buildings) {
      g.save();
      g.translate(this.x(b.center.x), this.z(b.center.z));
      // heading convention: rotation.y = angle; on the XZ bitmap that is a rotation of -angle.
      g.rotate(-b.angle);
      f(b.halfX * this.S, b.halfZ * this.S, b);
      g.restore();
    }
  }

  /** Stroke every road of the classes in `width` (px per class; a class left out is skipped). */
  roads(width: (cls: RoadClass, e: RoadEdge) => number | null, colour: (cls: RoadClass, e: RoadEdge) => string, cap: CanvasLineCap = 'round', dash?: number[]): void {
    const g = this.g;
    const nodes = this.L.roads.nodes;
    g.lineCap = cap;
    g.lineJoin = 'round';
    if (dash) g.setLineDash(dash);
    for (const e of this.L.roads.edges) {
      const cls = roadClass(e);
      const w = width(cls, e);
      if (w === null) continue;
      const a = nodes[e.from]!.pos;
      const b = nodes[e.to]!.pos;
      g.strokeStyle = colour(cls, e);
      g.lineWidth = Math.max(1, w);
      g.beginPath();
      g.moveTo(this.x(a.x), this.z(a.z));
      g.lineTo(this.x(b.x), this.z(b.z));
      g.stroke();
    }
    if (dash) g.setLineDash([]);
  }

  /** The viaduct: a dark band with sleepers across it. */
  rail(fill: string, ties: string): void {
    this.features('rail', (r) => {
      this.fillRect(r, fill);
      const g = this.g;
      const [x, y, w, h] = this.rect(r);
      g.strokeStyle = ties;
      g.lineWidth = Math.max(1, 0.9 * this.S);
      g.beginPath();
      const vertical = h > w;
      const step = 4 * this.S;
      if (vertical) for (let t = y + step / 2; t < y + h; t += step) (g.moveTo(x + w * 0.15, t), g.lineTo(x + w * 0.85, t));
      else for (let t = x + step / 2; t < x + w; t += step) (g.moveTo(t, y + h * 0.15), g.lineTo(t, y + h * 0.85));
      g.stroke();
    });
  }

  /** Water with a lighter shore line and a few ripples. */
  water(fill: string, shore: string, ripple: string | null): void {
    this.features('water', (r) => {
      this.fillRect(r, fill);
      this.strokeRect(r, shore, Math.max(1, 0.8 * this.S));
      if (!ripple) return;
      const g = this.g;
      const [x, y, w, h] = this.rect(r);
      g.save();
      g.beginPath();
      g.rect(x, y, w, h);
      g.clip();
      g.strokeStyle = ripple;
      g.lineWidth = Math.max(1, 0.5 * this.S);
      const n = Math.round((w * h) / (this.S * this.S) / 260);
      for (let i = 0; i < n; i++) {
        const cx = x + this.rnd() * w;
        const cy = y + this.rnd() * h;
        const len = (4 + this.rnd() * 6) * this.S;
        g.beginPath();
        g.moveTo(cx, cy);
        g.quadraticCurveTo(cx + len * 0.5, cy - 1.2 * this.S, cx + len, cy);
        g.stroke();
      }
      g.restore();
    });
  }
}

// --------------------------------------------------------------------------- classic

function paintClassic(p: Painter): void {
  const { g, S, L } = p;
  p.fillAll('#1d1a17');
  L.districts.forEach((d, i) => p.fillRect(d.bounds, i % 2 === 0 ? 'rgba(255,240,200,0.035)' : 'rgba(120,150,200,0.04)'));
  p.blocks((hx, hz) => {
    g.fillStyle = '#2f2a25';
    g.strokeStyle = '#24201c';
    g.lineWidth = Math.max(1, S * 0.5);
    g.fillRect(-hx, -hz, hx * 2, hz * 2);
    g.strokeRect(-hx, -hz, hx * 2, hz * 2);
  });
  p.roads((cls, e) => (e.width + 1.6) * S * (cls === 'alley' ? 0.9 : 1), () => '#0e0c0a');
  p.roads((cls, e) => e.width * S * (cls === 'alley' ? 0.9 : 1), (cls) => (cls === 'alley' ? '#6e634b' : '#a79a7a'));
  p.roads((cls, e) => (cls === 'alley' || e.oneWay ? null : Math.max(1, S * 0.35)), () => 'rgba(21,18,15,0.35)', 'butt', [S * 2.2, S * 2.2]);
}

// --------------------------------------------------------------------------- night (modern dark)

function paintNight(p: Painter): void {
  const { g, S, L } = p;
  p.fillAll('#13161b');
  L.districts.forEach((d, i) => p.fillRect(d.bounds, i % 3 === 0 ? 'rgba(255,255,255,0.02)' : i % 3 === 1 ? 'rgba(110,150,255,0.035)' : 'rgba(255,200,120,0.025)'));
  p.features('yard', (r) => {
    p.fillRect(r, '#191c22');
    p.hatch(r, 'rgba(255,255,255,0.045)', 5, Math.max(1, 0.5 * S));
  });
  p.features('site', (r) => {
    p.fillRect(r, '#1d1a16');
    p.hatch(r, 'rgba(255,190,90,0.12)', 4, Math.max(1, 0.5 * S));
  });
  p.features('green', (r) => {
    p.fillRect(r, '#1a2a20');
    p.dots(r, 'rgba(70,120,70,0.35)', 0.02, 0.9);
    p.dots(r, '#223a26', 0.0045, 2.6, 'rgba(110,170,100,0.28)');
  });
  p.water('#16263a', 'rgba(120,180,230,0.35)', 'rgba(140,185,225,0.14)');
  p.rail('#0a0b0e', 'rgba(255,255,255,0.16)');
  // Blocks as volumes: the painted roofs muted into the night (slate for the pitched, tar for the sheds,
  // concrete for the flats), a shadow cast to the south-east by their height, a lit north-west edge.
  p.blocks((hx, hz, b) => {
    const d = Math.min(5, 0.9 + b.height * 0.14) * S;
    g.fillStyle = 'rgba(0,0,0,0.42)';
    g.save();
    g.rotate(b.angle);
    g.translate(d * 0.7, d);
    g.rotate(-b.angle);
    g.fillRect(-hx, -hz, hx * 2, hz * 2);
    g.restore();
  });
  p.blocks((hx, hz, b) => {
    const [lit, dark, kind] = NIGHT_ROOF[b.style] ?? NIGHT_ROOF_DEFAULT;
    const along = hx >= hz;
    g.fillStyle = lit;
    g.fillRect(-hx, -hz, hx * 2, hz * 2);
    if (kind === 'pitched') {
      // the shaded slope, so a pitched roof reads as one even zoomed out
      g.fillStyle = dark;
      if (along) g.fillRect(-hx, 0, hx * 2, hz);
      else g.fillRect(0, -hz, hx, hz * 2);
    }
    g.strokeStyle = 'rgba(255,255,255,0.13)';
    g.lineWidth = Math.max(1, 0.45 * S);
    g.beginPath();
    g.moveTo(-hx, hz);
    g.lineTo(-hx, -hz);
    g.lineTo(hx, -hz);
    g.stroke();
  });
  // Roads: a casing, a soft glow under the main roads, fills by class, centre dashes on the mains.
  p.roads((cls, e) => (e.width + 2) * S * (cls === 'alley' ? 0.85 : 1), () => '#090a0d');
  p.roads((cls, e) => (cls === 'main' ? (e.width + 7) * S : null), () => 'rgba(210,220,235,0.07)');
  p.roads((cls, e) => (cls === 'main' ? (e.width + 3) * S : null), () => 'rgba(210,220,235,0.12)');
  p.roads(
    (cls, e) => e.width * S * (cls === 'alley' ? 0.85 : 1),
    (cls) => (cls === 'main' ? '#d9dde3' : cls === 'street' ? '#8d939b' : cls === 'narrow' ? '#6c7178' : '#43484f'),
  );
  p.roads((cls) => (cls === 'main' ? Math.max(1, S * 0.3) : null), () => 'rgba(19,22,27,0.55)', 'butt', [S * 2.5, S * 2.5]);
}

// --------------------------------------------------------------------------- print (1970 A–Z)

const PRINT_TINTS = ['#f5e6b4', '#f3d7cb', '#dde8cb', '#d8e2ee', '#efdcc4', '#e5ddee', '#f0e4bd', '#dfe6d6'];

function paintPrint(p: Painter): void {
  const { g, S, L } = p;
  p.fillAll('#efe7d3');
  L.districts.forEach((d, i) => p.fillRect(d.bounds, PRINT_TINTS[i % PRINT_TINTS.length]!));
  p.features('yard', (r) => {
    p.fillRect(r, '#e4dcc8');
    p.hatch(r, 'rgba(120,105,85,0.35)', 3.5, Math.max(1, 0.4 * S));
  });
  p.features('site', (r) => {
    p.fillRect(r, '#ecd9b2');
    p.hatch(r, 'rgba(160,110,40,0.35)', 3.5, Math.max(1, 0.4 * S));
  });
  p.features('green', (r) => {
    p.fillRect(r, '#cfe2ad');
    p.dots(r, 'rgba(90,140,60,0.55)', 0.004, 2);
  });
  p.water('#b8d6ea', '#5c98c2', 'rgba(255,255,255,0.45)');
  p.rail('#2a2420', '#efe7d3');
  // Blocks: the atlas's grey-pink with a fine edge.
  p.blocks((hx, hz, b) => {
    g.fillStyle = b.style === 'church' ? '#c9bfd9' : b.height > 16 ? '#cfc2b0' : '#dacfbf';
    g.fillRect(-hx, -hz, hx * 2, hz * 2);
    g.strokeStyle = 'rgba(90,78,64,0.7)';
    g.lineWidth = Math.max(1, 0.35 * S);
    g.strokeRect(-hx, -hz, hx * 2, hz * 2);
  });
  // Roads: black casing, A-roads in orange, the rest white; alleys thin and cream.
  p.roads((cls, e) => (e.width + (cls === 'alley' ? 1 : 1.8)) * S * (cls === 'alley' ? 0.8 : 1), (cls) => (cls === 'alley' ? '#8c8172' : '#221c18'));
  p.roads(
    (cls, e) => e.width * S * (cls === 'alley' ? 0.8 : 1),
    (cls) => (cls === 'main' ? '#f2b23a' : cls === 'street' ? '#ffffff' : cls === 'narrow' ? '#fbf6ea' : '#ebe4d4'),
  );
}

// --------------------------------------------------------------------------- painted (illustrated top-down)

const ROOF: Partial<Record<BuildingLot['style'], [string, string, 'pitched' | 'flat' | 'saw']>> = {
  terrace: ['#5d6270', '#4a4f5b', 'pitched'],
  shopfront: ['#605a55', '#4e4944', 'pitched'],
  pub: ['#6b5a52', '#574843', 'pitched'],
  cafe: ['#6b5a52', '#574843', 'pitched'],
  church: ['#5f6472', '#4b505c', 'pitched'],
  warehouse: ['#6f6659', '#5c5449', 'saw'],
  factory: ['#6a6256', '#575047', 'saw'],
  shed: ['#766b5c', '#62594c', 'saw'],
  flats: ['#8a8278', '#8a8278', 'flat'],
  office: ['#85807a', '#85807a', 'flat'],
  bank: ['#7e7a74', '#7e7a74', 'flat'],
  station: ['#7a776f', '#7a776f', 'flat'],
  lockup: ['#6c6961', '#6c6961', 'flat'],
  garage: ['#6c6961', '#6c6961', 'flat'],
  club: ['#6a6058', '#564d47', 'pitched'],
};

function paintPainted(p: Painter): void {
  const { g, S, L } = p;
  p.fillAll('#3a3731');
  // Yards and sites: packed earth and rubble.
  p.features('yard', (r) => {
    p.fillRect(r, '#55504a');
    p.hatch(r, 'rgba(0,0,0,0.12)', 6, Math.max(1, 0.6 * S));
  });
  p.features('site', (r) => {
    p.fillRect(r, '#5c4b38');
    p.dots(r, 'rgba(120,100,70,0.6)', 0.01, 1.2);
  });
  // Greens: grass with plane trees.
  p.features('green', (r) => {
    p.fillRect(r, '#4f6b3a');
    p.dots(r, 'rgba(60,90,40,0.5)', 0.02, 1);
    p.dots(r, '#3b5a2a', 0.0045, 2.6, 'rgba(140,180,90,0.45)');
  });
  p.water('#27465b', 'rgba(170,200,220,0.45)', 'rgba(170,205,230,0.32)');
  p.rail('#26231f', '#58514a');
  // Pavements under the roads (the kerb to kerb width plus the sidewalks), then the carriageway.
  p.roads((cls, e) => (cls === 'alley' ? null : (e.width + e.sidewalk * 2 + 0.6) * S), () => '#8a8275');
  p.roads((cls, e) => (cls === 'alley' ? null : (e.width + 0.9) * S), () => '#2d2a25');
  p.roads((cls, e) => e.width * S * (cls === 'alley' ? 0.85 : 1), (cls) => (cls === 'main' ? '#4b4741' : cls === 'alley' ? '#3f3b35' : '#45413b'));
  p.roads((cls, e) => (cls === 'main' && !e.oneWay ? Math.max(1, S * 0.28) : null), () => 'rgba(232,220,185,0.45)', 'butt', [S * 2.5, S * 2.5]);
  // Blocks: shadows cast to the south-east by their height, then the roofs.
  p.blocks((hx, hz, b) => {
    const d = Math.min(6, 1.2 + b.height * 0.16) * S;
    g.fillStyle = 'rgba(0,0,0,0.38)';
    g.save();
    g.rotate(b.angle);
    g.translate(d * 0.7, d);
    g.rotate(-b.angle);
    g.fillRect(-hx, -hz, hx * 2, hz * 2);
    g.restore();
  });
  p.blocks((hx, hz, b) => {
    const [lit, dark, kind] = ROOF[b.style] ?? ['#6e6a62', '#5b5750', 'flat'];
    const along = hx >= hz; // the ridge runs along the long side
    if (kind === 'pitched') {
      g.fillStyle = lit;
      if (along) g.fillRect(-hx, -hz, hx * 2, hz);
      else g.fillRect(-hx, -hz, hx, hz * 2);
      g.fillStyle = dark;
      if (along) g.fillRect(-hx, 0, hx * 2, hz);
      else g.fillRect(0, -hz, hx, hz * 2);
      g.strokeStyle = 'rgba(190,195,205,0.5)';
      g.lineWidth = Math.max(1, 0.35 * S);
      g.beginPath();
      if (along) (g.moveTo(-hx, 0), g.lineTo(hx, 0));
      else (g.moveTo(0, -hz), g.lineTo(0, hz));
      g.stroke();
      // chimney pots along the ridge
      if (b.style === 'terrace' || b.style === 'pub' || b.style === 'shopfront') {
        g.fillStyle = '#3a3632';
        const len = along ? hx : hz;
        const step = 7 * S;
        for (let t = -len + step / 2; t < len; t += step) {
          const cx = along ? t : 0;
          const cz = along ? 0 : t;
          g.fillRect(cx - 0.6 * S, cz - 0.6 * S, 1.2 * S, 1.2 * S);
        }
      }
    } else if (kind === 'saw') {
      g.fillStyle = dark;
      g.fillRect(-hx, -hz, hx * 2, hz * 2);
      g.fillStyle = lit;
      const step = 5 * S;
      if (along) for (let t = -hx; t < hx; t += step) g.fillRect(t, -hz, step * 0.55, hz * 2);
      else for (let t = -hz; t < hz; t += step) g.fillRect(-hx, t, hx * 2, step * 0.55);
    } else {
      g.fillStyle = lit;
      g.fillRect(-hx, -hz, hx * 2, hz * 2);
      g.strokeStyle = 'rgba(0,0,0,0.25)';
      g.lineWidth = Math.max(1, 0.8 * S);
      g.strokeRect(-hx + 0.6 * S, -hz + 0.6 * S, hx * 2 - 1.2 * S, hz * 2 - 1.2 * S);
    }
    // the sunlit north-west edge, the shaded south-east one
    g.lineWidth = Math.max(1, 0.5 * S);
    g.strokeStyle = 'rgba(255,220,170,0.45)';
    g.beginPath();
    g.moveTo(-hx, hz);
    g.lineTo(-hx, -hz);
    g.lineTo(hx, -hz);
    g.stroke();
    g.strokeStyle = 'rgba(0,0,0,0.45)';
    g.beginPath();
    g.moveTo(hx, -hz);
    g.lineTo(hx, hz);
    g.lineTo(-hx, hz);
    g.stroke();
  });
  // Dusk: warm light from the west, a cool blue creeping in from the east; street lamps along the mains.
  const grad = g.createLinearGradient(0, 0, p.W, p.H * 0.4);
  grad.addColorStop(0, 'rgba(255,190,120,0.10)');
  grad.addColorStop(1, 'rgba(40,60,120,0.16)');
  g.fillStyle = grad;
  g.fillRect(0, 0, p.W, p.H);
  const nodes = L.roads.nodes;
  for (const e of L.roads.edges) {
    const cls = roadClass(e);
    if (cls === 'alley' || cls === 'narrow') continue;
    const a = nodes[e.from]!.pos;
    const b2 = nodes[e.to]!.pos;
    const len = Math.hypot(b2.x - a.x, b2.z - a.z);
    const n = Math.floor(len / 34);
    for (let i = 1; i <= n; i++) {
      const t = i / (n + 1);
      const cx = p.x(a.x + (b2.x - a.x) * t);
      const cz = p.z(a.z + (b2.z - a.z) * t);
      const lamp = g.createRadialGradient(cx, cz, 0, cx, cz, 4.5 * S);
      lamp.addColorStop(0, 'rgba(255,210,130,0.22)');
      lamp.addColorStop(1, 'rgba(255,210,130,0)');
      g.fillStyle = lamp;
      g.beginPath();
      g.arc(cx, cz, 4.5 * S, 0, Math.PI * 2);
      g.fill();
    }
  }
}

// --------------------------------------------------------------------------- night roofs (A with C's roofs)

/** Roof colours per style for the night map: lit slope, shaded slope, and how the detail pass treats it. */
type RoofKind = 'pitched' | 'flat' | 'saw';
const NIGHT_ROOF: Partial<Record<BuildingLot['style'], [string, string, RoofKind]>> = {
  terrace: ['#2e333d', '#262a33', 'pitched'],
  shopfront: ['#30333a', '#282b31', 'pitched'],
  pub: ['#36323a', '#2c2930', 'pitched'],
  cafe: ['#36323a', '#2c2930', 'pitched'],
  club: ['#34313a', '#2b2930', 'pitched'],
  church: ['#30343f', '#272a33', 'pitched'],
  warehouse: ['#34332f', '#2b2a27', 'saw'],
  factory: ['#33322e', '#2a2926', 'saw'],
  shed: ['#37352f', '#2d2b27', 'saw'],
  flats: ['#3c3f45', '#3c3f45', 'flat'],
  office: ['#3a3d43', '#3a3d43', 'flat'],
  bank: ['#393c42', '#393c42', 'flat'],
  station: ['#383b40', '#383b40', 'flat'],
  lockup: ['#34363a', '#34363a', 'flat'],
  garage: ['#34363a', '#34363a', 'flat'],
};
const NIGHT_ROOF_DEFAULT: [string, string, RoofKind] = ['#33363c', '#2a2d33', 'flat'];

/** The full-map zoom (relative to "the whole borough fits") where the roof detail starts, and where it is all in. */
export const ROOF_DETAIL_FROM = 2.0;
export const ROOF_DETAIL_FULL = 4.5;

/** How much roof detail to draw at a relative zoom: 0 (calm, zoomed out) … 1 (all of it, close in). */
export function roofDetailAlpha(rel: number): number {
  return Math.min(1, Math.max(0, (rel - ROOF_DETAIL_FROM) / (ROOF_DETAIL_FULL - ROOF_DETAIL_FROM)));
}

export interface ScreenMap {
  toScreenX(x: number): number;
  toScreenY(z: number): number;
  zoom: number;
  w: number;
  h: number;
}

/**
 * The roof detail on the full map, drawn from the layout over the bitmap: ridges, chimney pots, sawtooth bays,
 * the flat roofs' parapets, lamps along the main roads — only the blocks on screen, with `alpha` from
 * `roofDetailAlpha`. Nothing is allocated; the full map draws at most 15 times a second and only while open.
 */
export function drawRoofDetail(g: CanvasRenderingContext2D, L: CityLayout, cam: ScreenMap, alpha: number): void {
  if (alpha <= 0) return;
  const z = cam.zoom;
  g.save();
  g.globalAlpha = alpha;
  g.lineCap = 'butt';
  for (const b of L.buildings) {
    const r = Math.hypot(b.halfX, b.halfZ) * z;
    const sx = cam.toScreenX(b.center.x);
    const sy = cam.toScreenY(b.center.z);
    if (sx < -r || sy < -r || sx > cam.w + r || sy > cam.h + r) continue;
    const kind = (NIGHT_ROOF[b.style] ?? NIGHT_ROOF_DEFAULT)[2];
    const hx = b.halfX * z;
    const hz = b.halfZ * z;
    const along = hx >= hz;
    g.save();
    g.translate(sx, sy);
    g.rotate(-b.angle);
    if (kind === 'pitched') {
      g.strokeStyle = 'rgba(220,228,240,0.22)';
      g.lineWidth = Math.max(1, 0.35 * z);
      g.beginPath();
      if (along) (g.moveTo(-hx, 0), g.lineTo(hx, 0));
      else (g.moveTo(0, -hz), g.lineTo(0, hz));
      g.stroke();
      if (b.style === 'terrace' || b.style === 'pub' || b.style === 'shopfront') {
        g.fillStyle = 'rgba(0,0,0,0.5)';
        const len = along ? hx : hz;
        const step = 7 * z;
        const c = 0.6 * z;
        for (let t = -len + step / 2; t < len; t += step) {
          if (along) g.fillRect(t - c, -c, c * 2, c * 2);
          else g.fillRect(-c, t - c, c * 2, c * 2);
        }
      }
    } else if (kind === 'saw') {
      g.fillStyle = 'rgba(255,255,255,0.06)';
      const step = 5 * z;
      if (along) for (let t = -hx; t < hx; t += step) g.fillRect(t, -hz, step * 0.55, hz * 2);
      else for (let t = -hz; t < hz; t += step) g.fillRect(-hx, t, hx * 2, step * 0.55);
    } else {
      g.strokeStyle = 'rgba(0,0,0,0.28)';
      g.lineWidth = Math.max(1, 0.7 * z);
      const i = 0.8 * z;
      g.strokeRect(-hx + i, -hz + i, hx * 2 - i * 2, hz * 2 - i * 2);
    }
    g.restore();
  }
  // Street lamps along the main roads: a warm point and a faint pool, once the street is close enough.
  const lampA = Math.max(0, (alpha - 0.4) / 0.6);
  if (lampA > 0) {
    const nodes = L.roads.nodes;
    g.globalAlpha = lampA;
    for (const e of L.roads.edges) {
      if (e.alley || !e.main) continue;
      const a = nodes[e.from]!.pos;
      const b = nodes[e.to]!.pos;
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      const n = Math.floor(len / 30);
      for (let i = 1; i <= n; i++) {
        const t = i / (n + 1);
        const x = cam.toScreenX(a.x + (b.x - a.x) * t);
        const y = cam.toScreenY(a.z + (b.z - a.z) * t);
        if (x < -20 || y < -20 || x > cam.w + 20 || y > cam.h + 20) continue;
        g.fillStyle = 'rgba(255,205,130,0.10)';
        g.beginPath();
        g.arc(x, y, 4.5 * z, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = 'rgba(255,225,170,0.85)';
        g.beginPath();
        g.arc(x, y, Math.max(1, 0.45 * z), 0, Math.PI * 2);
        g.fill();
      }
    }
  }
  g.restore();
}
